import { useRef, useEffect, useState } from 'react';
import { interpolate, useCurrentFrame, useVideoConfig, Easing } from 'remotion';

// Types
import type { PositionData } from './types';

// Constants
import { RIBBON_CONFIG, PATH_DATA, TRANSFORM_CONFIG } from './constants';

// Utils
import { SwallowTailCap } from './utils';

// Theme
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

const RIBBON_COLORS = employeeYearInReviewTheme.rawColors.ribbon;

export const RibbonAnimation = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const pathRef = useRef<SVGPathElement>(null);
  const [pathLength, setPathLength] = useState(0);

  // Initialize path length on mount
  useEffect(() => {
    if (pathRef.current) {
      setPathLength(pathRef.current.getTotalLength());
    }
  }, []);

  // Animate progress from 0 to MAX_PROGRESS with easing
  const progressT = interpolate(
    frame,
    [0, RIBBON_CONFIG.ANIMATION_FRAMES],
    [0, RIBBON_CONFIG.MAX_PROGRESS],
    { extrapolateRight: 'clamp', easing: Easing.out(Easing.ease) },
  );

  // Get position & angle at any point along the path
  const getPositionAtProgress = (progress: number): PositionData | null => {
    if (!pathRef.current || pathLength === 0) return null;

    // Convert progress (0-1) to distance along path
    const distance = Math.min(progress * pathLength, pathLength);
    const point = pathRef.current.getPointAtLength(distance);

    // Calculate tangent angle using nearby points
    const delta = RIBBON_CONFIG.ANGLE_CALCULATION_DELTA;
    const prevPoint = pathRef.current.getPointAtLength(
      Math.max(0, distance - delta),
    );
    const angle = Math.atan2(point.y - prevPoint.y, point.x - prevPoint.x);

    return { x: point.x, y: point.y, angle };
  };

  // Head position (front of ribbon)
  const headLen = Math.min(progressT, 1);
  // Tail position (back of ribbon) - starts appearing after RIBBON_VISIBLE_LENGTH
  const tailLen = Math.max(0, progressT - RIBBON_CONFIG.RIBBON_VISIBLE_LENGTH);

  const headPos = getPositionAtProgress(headLen);
  const tailPos = getPositionAtProgress(tailLen);

  // Setup stroke-dasharray for progressive reveal effect
  const currentVisibleLength =
    Math.max(0, (headLen - tailLen) * pathLength) +
    RIBBON_CONFIG.OVERLAP_BUFFER;
  const dashArray = `${currentVisibleLength} ${pathLength * 3}`;
  const dashOffset = -tailLen * pathLength;

  // Hide ribbon when animation completes
  const isVisible = progressT < RIBBON_CONFIG.MAX_PROGRESS;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 10 }}
    >
      <defs>
        {/* Body gradient (vertical) */}
        <linearGradient
          id="ribbonBodyGradient"
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
        >
          <stop offset="0%" stopColor={RIBBON_COLORS.body} />
          <stop offset="100%" stopColor={RIBBON_COLORS.body} />
        </linearGradient>

        {/* Sheen gradient (horizontal) */}
        <linearGradient
          id="globalSheen"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2={width}
          y2="0"
        >
          <stop
            offset="0%"
            stopColor={RIBBON_COLORS.sheenWhite}
            stopOpacity={0}
          />
          <stop
            offset="50%"
            stopColor={RIBBON_COLORS.sheenWhite}
            stopOpacity={0.3}
          />
          <stop
            offset="100%"
            stopColor={RIBBON_COLORS.sheenWhite}
            stopOpacity={0}
          />
        </linearGradient>
      </defs>

      {/* Apply transform and shadow */}
      <g
        transform={`translate(${TRANSFORM_CONFIG.TRANSLATE_X}, ${TRANSFORM_CONFIG.TRANSLATE_Y}) scale(${TRANSFORM_CONFIG.SCALE_X}, ${TRANSFORM_CONFIG.SCALE_Y})`}
      >
        {/* Hidden reference path for calculations */}
        <path
          ref={pathRef}
          d={PATH_DATA}
          fill="none"
          stroke="none"
          style={{ visibility: 'hidden', position: 'absolute' }}
        />

        {/* Layer 2: Body (inset from border) */}
        <path
          d={PATH_DATA}
          fill="none"
          stroke="url(#ribbonBodyGradient)"
          strokeWidth={RIBBON_CONFIG.RIBBON_WIDTH}
          strokeLinecap="butt"
          strokeDasharray={dashArray}
          strokeDashoffset={dashOffset}
          opacity={isVisible ? 1 : 0}
        />

        {/* Layer 3: Sheen (overlay blend mode) */}
        <path
          d={PATH_DATA}
          fill="none"
          stroke="url(#globalSheen)"
          strokeWidth={RIBBON_CONFIG.RIBBON_WIDTH}
          strokeLinecap="butt"
          strokeDasharray={dashArray}
          strokeDashoffset={dashOffset}
          style={{ mixBlendMode: 'overlay' }}
        />

        {/* Head cap - visible while ribbon is drawing */}
        {headPos && progressT > 0 && headLen < 0.99 && (
          <SwallowTailCap
            type="head"
            point={{ x: headPos.x, y: headPos.y }}
            angle={headPos.angle}
            width={RIBBON_CONFIG.RIBBON_WIDTH}
          />
        )}

        {/* Tail cap - appears once head has traveled enough distance */}
        {tailPos &&
          progressT > RIBBON_CONFIG.RIBBON_VISIBLE_LENGTH &&
          isVisible &&
          tailLen < 0.99 && (
            <SwallowTailCap
              type="tail"
              point={{ x: tailPos.x, y: tailPos.y }}
              angle={tailPos.angle}
              width={RIBBON_CONFIG.RIBBON_WIDTH}
            />
          )}
      </g>
    </svg>
  );
};
